'use client';

import { useCallback, useEffect, useMemo, useState } from 'react';
import { Button } from '@/components';
import ChevronDownIcon from '@/components/icons/ChevronDownIcon';
import SortIcon from '@/components/icons/SortIcon';
import ReviewTestimonialCard, {
  type ReviewCardTestimonial,
} from '@/components/reviews/ReviewTestimonialCard';
import type { GooglePlaceReviewRow } from '@/lib/api/store-place-details';
import { fetchStorePlaceDetailsForReviews } from '@/lib/api/store-place-details';
import type { Store } from '@/lib/types/stores';
import { useSearchSortDropdown } from '@/app/search/useSearchSortDropdown';

const REVIEW_SORT_OPTIONS = [
  { value: 'newest' as const, label: 'Newest' },
  { value: 'oldest' as const, label: 'Oldest' },
  { value: 'highest' as const, label: 'Highest rated' },
];

type SortValue = (typeof REVIEW_SORT_OPTIONS)[number]['value'];
type ReviewWithStore = GooglePlaceReviewRow & { storeLabel?: string };

function storeChipLabel(store: Store): string {
  const name = store.name?.trim();
  if (name) return name;
  const city = store.city?.trim();
  if (city) return city;
  return `Store ${store.covaLocationId}`;
}

function dedupeReviews(rows: ReviewWithStore[]): ReviewWithStore[] {
  const seen = new Set<string>();
  const out: ReviewWithStore[] = [];
  for (const r of rows) {
    const k = `${r.time}|${r.author_name}|${r.text}`.toLowerCase();
    if (seen.has(k)) continue;
    seen.add(k);
    out.push(r);
  }
  return out;
}

function toTestimonial(r: ReviewWithStore): ReviewCardTestimonial {
  const time = r.storeLabel ? `${r.relative_time_description} · ${r.storeLabel}` : r.relative_time_description;
  return {
    name: r.author_name,
    time,
    rating: r.rating,
    review: r.text,
    avatar: r.profile_photo_url,
  };
}

type Props = {
  stores: Store[];
  apiBase: string;
};

const ALL_LOCATIONS_KEY = '_all_locations_';
const COLLAPSED_MAX_ROWS = 3;
const MIN_TO_COLLAPSE = COLLAPSED_MAX_ROWS * 3;

export default function ReviewsBrowseSection({ stores, apiBase }: Props) {
  const { sortOpen, setSortOpen, sortWrapRef } = useSearchSortDropdown();
  const [selectedKey, setSelectedKey] = useState<string>(ALL_LOCATIONS_KEY);
  const [sortBy, setSortBy] = useState<SortValue>('newest');
  const [rows, setRows] = useState<ReviewWithStore[]>([]);
  const [loading, setLoading] = useState(false);
  const [showAll, setShowAll] = useState(false);

  /** One chip per store (all locations), not grouped by city. */
  const filters = useMemo(() => {
    const validStores = stores.filter(
      (s) => Number.isFinite(Number(s.covaLocationId)) && Number(s.covaLocationId) > 0
    );
    const sorted = [...validStores].sort((a, b) =>
      storeChipLabel(a).localeCompare(storeChipLabel(b), undefined, { sensitivity: 'base' })
    );
    const allIds = [...new Set(sorted.map((s) => Number(s.covaLocationId)))];
    return [
      { key: ALL_LOCATIONS_KEY, label: 'All locations', locationIds: allIds },
      ...sorted.map((s) => ({
        key: `loc_${s.covaLocationId}`,
        label: storeChipLabel(s),
        locationIds: [Number(s.covaLocationId)],
      })),
    ];
  }, [stores]);

  const activeFilter = useMemo(
    () => filters.find((f) => f.key === selectedKey) ?? filters[0],
    [filters, selectedKey]
  );

  const storeLabelById = useMemo(() => {
    const map = new Map<number, string>();
    for (const store of stores) {
      const id = Number(store.covaLocationId);
      if (!Number.isFinite(id) || id <= 0) continue;
      const city = String(store.city || '').trim();
      const name = String(store.name || '').trim();
      if (city && name && city.toLowerCase() !== name.toLowerCase()) {
        map.set(id, `${city} (${name})`);
      } else if (city) {
        map.set(id, city);
      } else if (name) {
        map.set(id, name);
      }
    }
    return map;
  }, [stores]);

  const fetchAllReviewsForStore = useCallback(
    async (storeId: number): Promise<ReviewWithStore[]> => {
      const merged: ReviewWithStore[] = [];
      const seenTokens = new Set<string>();
      let nextPageToken: string | undefined;
      const maxPages = 20;
      const storeLabel = storeLabelById.get(storeId);
      for (let page = 0; page < maxPages; page += 1) {
        const data = await fetchStorePlaceDetailsForReviews(apiBase, storeId, 50, nextPageToken);
        if (!data?.reviews?.length) break;
        for (const row of data.reviews) {
          merged.push({ ...row, storeLabel });
        }
        const token = String(data.nextPageToken || '').trim();
        if (!token || seenTokens.has(token)) break;
        seenTokens.add(token);
        nextPageToken = token;
      }
      return merged;
    },
    [apiBase, storeLabelById]
  );

  const loadReviews = useCallback(async () => {
    const ids = activeFilter?.locationIds ?? [];
    if (ids.length === 0) {
      setRows([]);
      return;
    }
    setLoading(true);
    try {
      const results = await Promise.all(ids.map((id) => fetchAllReviewsForStore(id)));
      const merged: ReviewWithStore[] = [];
      for (const rowsForStore of results) {
        if (rowsForStore.length) merged.push(...rowsForStore);
      }
      setRows(dedupeReviews(merged));
    } catch {
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [activeFilter, fetchAllReviewsForStore]);

  useEffect(() => {
    void loadReviews();
  }, [loadReviews]);

  useEffect(() => {
    setShowAll(false);
  }, [selectedKey]);

  const sortedRows = useMemo(() => {
    const copy = [...rows];
    if (sortBy === 'newest') copy.sort((a, b) => (b.time || 0) - (a.time || 0));
    else if (sortBy === 'oldest') copy.sort((a, b) => (a.time || 0) - (b.time || 0));
    else copy.sort((a, b) => b.rating - a.rating || (b.time || 0) - (a.time || 0));
    return copy;
  }, [rows, sortBy]);

  const testimonials = useMemo(() => sortedRows.map(toTestimonial), [sortedRows]);

  const hasMore = testimonials.length > MIN_TO_COLLAPSE;

  const sortLabel = REVIEW_SORT_OPTIONS.find((o) => o.value === sortBy)?.label ?? 'Newest';

  const chipActive = (key: string) => selectedKey === key;

  return (
    <section className="w-full" aria-labelledby="reviews-browse-heading">
      <h2 id="reviews-browse-heading" className="sr-only">
        Browse customer reviews by location
      </h2>

      <div className="flex flex-col gap-6">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between lg:gap-6">
          <div className="flex min-w-0 flex-1 items-center gap-2 overflow-x-auto scrollbar-hide py-0.5">
            {filters.map((f) => {
              const active = chipActive(f.key);
              return (
                <Button
                  key={f.key}
                  variant={ active ? "primary" : "bordered" }
                  size="md"
                  onClick={() => setSelectedKey(f.key)}
                  className="rounded-xl"
                >
                  {f.label}
                </Button>
              );
            })}
          </div>

          <div className={`relative w-full shrink-0 lg:w-auto ${sortOpen ? 'z-50' : ''}`} ref={sortWrapRef}>
            <Button
              raw
              type="button"
              onClick={() => setSortOpen((v) => !v)}
              data-no-lift
              className={`w-max inline-flex items-center justify-center gap-2 rounded-xl border px-3 py-2.5 text-sm font-medium select-none transition-colors duration-200 ease-out focus:outline-none ${
                sortOpen
                  ? 'border-blue-10 bg-blue-5 text-blue-100'
                  : 'border-neutral-10 bg-constant-white text-text-secondary hover:border-blue-10 hover:bg-blue-5'
              }`}
            >
              <SortIcon size={16} color="currentColor" className="flex-shrink-0" />
              <span className="whitespace-nowrap">
                <span className={`${sortOpen ? 'text-blue-60' : 'text-text-tertiary'} font-normal`}>Sort:</span>{' '}
                <span className={`font-semibold ${sortOpen ? 'text-blue-100' : 'text-text-primary'}`}>{sortLabel}</span>
              </span>
              <ChevronDownIcon
                size={16}
                color="currentColor"
                className={`flex-shrink-0 transition-transform ${sortOpen ? 'rotate-180' : ''}`}
              />
            </Button>

            {sortOpen && (
              <div className="absolute right-0 z-50 mt-1.5 w-full min-w-[14rem] max-w-[20rem] overflow-hidden rounded-xl border border-neutral-10 bg-constant-white p-1.5 shadow-xl lg:right-0 lg:w-56">
                {REVIEW_SORT_OPTIONS.map((opt) => {
                  const active = opt.value === sortBy;
                  return (
                    <Button
                      key={opt.value}
                      variant="text"
                      color="primary"
                      size="md"
                      onClick={() => {
                        setSortBy(opt.value);
                        setSortOpen(false);
                      }}
                      className={`w-full !justify-start rounded-lg px-3 py-2.5 text-left text-sm ${
                        active
                          ? 'bg-blue-5 text-blue-100 font-semibold'
                          : 'font-medium'
                      }`}
                    >
                      {opt.label}
                    </Button>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        {loading ? (
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {Array.from({ length: 6 }).map((_, i) => (
              <div
                key={i}
                className="animate-pulse rounded-2xl border border-neutral-10 bg-constant-white p-4 sm:p-6"
              >
                <div className="flex gap-3">
                  <div className="h-14 w-14 shrink-0 rounded-xl bg-neutral-10" />
                  <div className="min-w-0 flex-1 space-y-2">
                    <div className="h-4 max-w-[66%] rounded bg-neutral-10" />
                    <div className="h-3 max-w-[40%] rounded bg-neutral-10" />
                  </div>
                </div>
                <div className="mt-4 h-px bg-neutral-10" />
                <div className="mt-4 space-y-2">
                  <div className="h-3 w-full rounded bg-neutral-10" />
                  <div className="h-3 w-full rounded bg-neutral-10" />
                  <div className="h-3 max-w-[80%] rounded bg-neutral-10" />
                </div>
              </div>
            ))}
          </div>
        ) : testimonials.length === 0 ? (
          <div className="rounded-2xl border border-neutral-10 bg-constant-white p-8 text-center text-sm text-text-secondary">
            No Google reviews loaded for this selection yet. Try another location or check back later.
          </div>
        ) : (
          <div className="space-y-6">
            <div className="relative">
              <div
                className={`grid grid-cols-1 items-stretch gap-4 sm:grid-cols-2 lg:grid-cols-3 ${
                  !showAll && hasMore ? 'max-h-[1060px] overflow-hidden sm:max-h-[920px]' : ''
                }`}
                role="list"
                aria-label="Customer reviews"
              >
                {testimonials.map((t, index) => (
                  <div key={`${t.name}-${t.time}-${index}`} className="flex h-full min-h-0 w-full" role="listitem">
                    <ReviewTestimonialCard testimonial={t} fillGridCell />
                  </div>
                ))}
              </div>

              {!showAll && hasMore && (
                <div
                  className="pointer-events-none absolute bottom-0 left-0 right-0"
                  style={{
                    background: 'linear-gradient(180deg, rgba(247, 247, 248, 0) -56.73%, #F7F7F8 79.82%)',
                    height: '275px',
                    width: '100%',
                  }}
                  aria-hidden
                />
              )}
            </div>

            {hasMore && (
              <div className="w-full">
                <Button
                  type="button"
                  onClick={() => setShowAll((v) => !v)}
                  variant="primary"
                  className="h-12 w-full rounded-xl border border-blue-80 bg-blue-80 px-6 text-body-16-desktop font-semibold text-constant-white transition-all duration-200 hover:border-neutral-70 hover:bg-blue-100 hover:!text-text-primary"
                >
                  {showAll ? 'Show less reviews' : 'View more reviews'}
                </Button>
              </div>
            )}
          </div>
        )}
      </div>
    </section>
  );
}
